import React, { useState, useEffect, useCallback } from "react";
import MaterialTable from "material-table";

import GYAxios from "../../../../util/GYAxios";
import { useClientUsersFormContext } from "../../../../util/clientUsersFormContext";

const ClientUsersTable = (props) => {
	const [users, setUsers] = useState([]);
	const [multiSites, setMultiSites] = useState([]);
	const width = 250;

	// client editing users context
	const { setSelectedClientUsers } = useClientUsersFormContext();

	/**
	 * Format Sites row
	 * @param {obj} rowData
	 * @returns formatted row
	 */
	const renderSitesRows = (rowData) =>
		rowData.site_desc !== "All Sites" ? (
			rowData.site_desc === "Multiple Sites" ? (
				<b>{rowData.site_desc} *</b>
			) : (
				<b>{rowData.site_desc}</b>
			)
		) : (
			rowData.site_desc
		);

	const initColumns = [
		{ title: "Email", width, field: "email" },
		{
			title: "Sites",
			width: 270,
			grouping: false,
			field: "site_desc",
			render: renderSitesRows,
		},
		{ title: "Username", width, field: "username" },
		{ title: "First Name", width, field: "first_name" },
		{ title: "Last Name", width, field: "last_name" },
		{ title: "Company", width, field: "company", grouping: true },
		{ title: "Position", width, grouping: false, field: "position" },
		{
			title: "Country",
			width: 100,
			grouping: true,
			field: "country",
			lookup: { TH: "Thailand", VN: "Vietnam" },
		},
		{
			title: "See Invoices",
			width: 100,
			grouping: false,
			field: "see_invoice",
			lookup: { 1: "YES", 0: "NO" },
		},
		{
			title: "See Reports",
			width: 100,
			grouping: false,
			field: "see_report",
			lookup: { 1: "YES", 0: "NO" },
		},
		{
			title: "Receiving e-invoice",
			width: 100,
			grouping: false,
			field: "receiving_einvoice",
			lookup: { 1: "YES", 0: "NO", 2: "N/A" },
		},
	];

	const tableOpts = {
		showTitle: false,
		grouping: true,
		pageSize: 10,
		pageSizeOptions: [10, 20, 40, 60],
		headerStyle: {
			fontSize: "1rem",
			textAlign: "center",
		},
		cellStyle: {
			textAlign: "center",
		},
		selection: props.usersSelectable, // 
	};

	/**
	 * Get multi sites of user
	 * @param {obj} rowData
	 * @returns multi sites of user
	 */
	const getMultiSites = (rowData) => {
		const siteIDs = rowData.site_id;
		const siteIDSplit = siteIDs.split(",");
		const noOfSites = siteIDSplit.length;
		const hasMultiSites = siteIDs != "all" && noOfSites > 1;
		const isMultiSiteUser = rowData.is_solo == 0 && hasMultiSites;

		// Get site info for multi site users
		if (isMultiSiteUser) {
			const multiSitesArray = getSites(rowData.site_id, multiSites);

			return (
				<ul
					style={{
						width: "260px",
						marginLeft: "245px",
					}}
				>
					{multiSitesArray.map((site) => {
						return (
							<li key={site.site_id}>
								<b>{site.site_name}</b>
							</li>
						);
					})}
				</ul>
			);
		}
	};

	/**
	 * Fetch data of multi sites
	 * @param {string} siteIDs
	 */
	const getMultiSitesData = (siteIDs) => {
		const siteURI = `/site/filter/site_id/${siteIDs}`;

		GYAxios.get(siteURI).then((res) => {
			const sites = !res.data.error ? res.data : [];

			setMultiSites(sites);
		});
	};

	/**
	 * Match users sites to multi site fetched
	 * @param {string} siteIDs
	 * @param {array} multiSites
	 * @returns site IDs from fetch multi sites
	 */
	const getSites = (siteIDs, multiSites) => {
		const siteIDArray = siteIDs.split(",");
		const siteArray = siteIDArray.map((siteID) => {
			const site = multiSites.find((site) => site.site_id == siteID);
			return site;
		});
		return siteArray;
	};

	/**
	 * Handle users
	 * @param {array} users
	 */
	const handleUsers = (users) => {
		const formattedUsers = users.map((user) => {
			let site_desc = "All Sites";

			// Show "Multiple Sites" only for HQ/Multisite clients
			if (user.site_id != "all" && user.is_solo == 0) {
				const siteIDSplit = user.site_id.split(",");
				const noOfSites = siteIDSplit.length;

				// User has multiple site IDs
				if (noOfSites > 1) site_desc = "Multiple Sites";
				// User has one site ID
				else site_desc = "Single Sites";
			}

			return { ...user, site_desc };
		});

		// Filter users with multi sites
		const multiSiteUsers = users.filter(
			(user) => user.site_id != "all" && user.is_solo == 0
		);
		// Get site ID of multi sites
		const siteIDs = multiSiteUsers.map((user) => user.site_id);
		const joinedSiteID = siteIDs.join(",");

		// Fetch data of multi sites of all users
		getMultiSitesData(joinedSiteID);
		setUsers(formattedUsers);
	};

	/**
	 * Material Table users select
	 * @param {Array<user>} rows
	 */
	const handleSelectUsers = useCallback(rows => {
		if(rows?.length) {
			setSelectedClientUsers(rows);
		} else {
			setSelectedClientUsers(null);
		}
	}, [ setSelectedClientUsers ]);

	/**
	 * Handle labels of single sites in table
	 * @param {obj} users
	 * @param {array} multiSites
	 */
	const handleSingleSites = (users, multiSites) => {
		const formattedUsers = users.map((user) => {
			if (user.site_desc === "Single Sites") {
				const siteID = user.site_id;
				const site = multiSites.find((site) => site.site_id == siteID);

				if (typeof site !== "undefined") user.site_desc = site.site_name;
			}

			return user;
		});

		setUsers(formattedUsers);
	};

	useEffect(() => {
		handleUsers(props.users);
	}, [props.users]);

	useEffect(() => {
		if (multiSites.length > 0) handleSingleSites(users, multiSites);
	}, [multiSites]);

	return (
		<div>
			<MaterialTable
				columns={initColumns}
				data={users}
				isLoading={props.isLoadingUsers}
				options={tableOpts}
				detailPanel={[
					{
						tooltip: "Show Multi Sites",
						render: (rowData) => getMultiSites(rowData),
					},
				]}
				onRowClick={(event, rowData, togglePanel) => togglePanel()}
				onSelectionChange={handleSelectUsers}
			/>
		</div>
	);
};

export default ClientUsersTable;
